import classNames from "classnames";
import { Field, FieldProps } from "formik";
import React from "react";
import { LuxInputLabel } from "./LuxInputLabel";

export type SwitcherOption<Value extends string | number> = {
  label: string;
  icon?: React.ReactNode;
  value: Value;
};

type ButtonSwitcherProps<Value extends string | number> = {
  className?: string;
  disabled?: boolean;
  hideIconsBreakpoint?: "tiny" | "small";
  maxWidth?: number | string;
  minWidth?: number | string;
  options: SwitcherOption<Value>[];
  round?: boolean;
  value: Value;
  setValue: (value: Value) => unknown;
  smallSizeBreakpoint?: "always" | "tiny" | "small";
};

type ButtonSwitcherFieldProps<Value extends string | number> = {
  className?: string;
  expand?: boolean;
  label?: string;
  name: string;
  onChange?: (option: Value) => void;
} & Omit<ButtonSwitcherProps<Value>, "value" | "setSelectedOption">;

export const ButtonSwitcher = <Value extends string | number>({
  className,
  disabled,
  hideIconsBreakpoint,
  maxWidth,
  minWidth,
  options,
  round,
  value,
  setValue,
  smallSizeBreakpoint,
}: ButtonSwitcherProps<Value>): JSX.Element => {
  let selectedIndex = -2;
  for (let i = 0; i < options.length; i++) {
    if (value === options[i].value) {
      selectedIndex = i;
    }
  }

  return (
    <div
      className={classNames(
        "lux-button-switcher",
        smallSizeBreakpoint,
        className,
        {
          [`hide-icons-${hideIconsBreakpoint}`]: hideIconsBreakpoint,
          round,
          disabled,
        },
      )}
      style={{
        minWidth: minWidth || "auto",
        maxWidth: maxWidth || "auto",
        // @ts-ignore
        "--option-length": options.length,
      }}
    >
      <div className="segments">
        {options.map((o, i) => (
          <button
            type="button"
            className={classNames("btn segment flex-center animated", {
              selected: i === selectedIndex,
              nodivider: i === selectedIndex || i - 1 === selectedIndex,
            })}
            key={i}
            onClick={() => {
              setValue(o.value);
            }}
          >
            {o.icon && <div className="flex-center icon">{o.icon}</div>}
            <div>{o.label}</div>
          </button>
        ))}
        <div
          className="slider animated"
          style={{
            left: `calc(100% / ${options.length} * ${selectedIndex})`,
          }}
        />
      </div>
    </div>
  );
};

export const ButtonSwitcherField = <Value extends string | number>({
  className,
  expand = true,
  label,
  name,
  onChange,
  options,
  ...props
}: Omit<ButtonSwitcherFieldProps<Value>, "value" | "setValue">) => (
  <div className={className}>
    {label && <LuxInputLabel text={label} />}
    <div className="flex">
      <Field name={name}>
        {({ field: { value }, form: { setFieldValue } }: FieldProps<Value>) => (
          <ButtonSwitcher<Value>
            className={classNames({ "flex-1": expand })}
            options={options}
            value={value}
            setValue={(nextOption) => {
              setFieldValue(name, nextOption);
              onChange?.(nextOption);
            }}
            {...props}
          />
        )}
      </Field>
    </div>
  </div>
);
